import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import {
  arTasks,
  concertTasks,
  foundationTasks,
  ipTasks,
  HubTask,
  TaskStep,
} from "../../../mocks/hubTasks";
import { campaigns } from "../../../mocks/campaigns";

interface Props {
  workspaceId: string;
}

type HubKey = "ar" | "concert" | "foundation" | "ip" | "campaign";

// Build campaign tasks from mock
const campaignTasks: HubTask[] = campaigns.flatMap(c =>
  c.tasks.map(t => ({
    id: t.id,
    title: `[${c.name}] ${t.title}`,
    detail: t.detail,
    dueDate: t.dueDate,
    dDay: t.dDay,
    status: t.status as HubTask["status"],
    assignee: t.assignee,
    steps: t.steps as TaskStep[],
    caution: t.caution,
  }))
);

interface HubConfig {
  label: string;
  shortLabel: string;
  icon: string;
  tasks: HubTask[];
}

const hubConfig: Record<HubKey, HubConfig> = {
  ar: { label: "A&R 크리에이티브 허브", shortLabel: "A&R", icon: "ri-music-2-line", tasks: arTasks },
  concert: { label: "공연기획 허브", shortLabel: "공연", icon: "ri-mic-line", tasks: concertTasks },
  foundation: { label: "재단·사업관리 허브", shortLabel: "재단", icon: "ri-building-line", tasks: foundationTasks },
  ip: { label: "IP 스튜디오", shortLabel: "IP", icon: "ri-copyright-line", tasks: ipTasks },
  campaign: { label: "캠페인 허브", shortLabel: "캠페인", icon: "ri-advertisement-line", tasks: campaignTasks },
};

interface OverrideRecord {
  task_id: string;
  hub: HubKey;
  title: string;
  detail: string;
  caution: string;
  reference_text: string;
  steps: TaskStep[];
}

type FormState = OverrideRecord;

interface WorkspaceOption { id: string; name: string; }

function StepEditor({
  step,
  idx,
  onChange,
  onRemove,
}: {
  step: TaskStep;
  idx: number;
  onChange: (idx: number, field: keyof TaskStep, val: string) => void;
  onRemove: (idx: number) => void;
}) {
  return (
    <div className="bg-[#252830] border border-[#2A2D3E] rounded-xl p-4 space-y-2.5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#E8375F]/15 text-[#E8375F] text-[11px] font-bold shrink-0">
            {step.num}
          </div>
          <span className="text-[#5A5E72] text-[11px]">Step {step.num}</span>
        </div>
        <button
          type="button"
          onClick={() => onRemove(idx)}
          className="w-6 h-6 flex items-center justify-center text-[#3E4155] hover:text-[#E8375F] transition-colors cursor-pointer rounded"
        >
          <i className="ri-delete-bin-line text-sm"></i>
        </button>
      </div>
      <input
        type="text"
        placeholder="단계 제목 *"
        value={step.title}
        onChange={(e) => onChange(idx, "title", e.target.value)}
        className="w-full bg-[#1C1F2E] text-white text-xs px-3 py-2 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder-[#3E4155]"
      />
      <textarea
        placeholder="상세 설명 (어떻게 해야 하는지 구체적으로 작성) *"
        value={step.detail}
        onChange={(e) => onChange(idx, "detail", e.target.value)}
        rows={3}
        className="w-full bg-[#1C1F2E] text-white text-xs px-3 py-2 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder-[#3E4155] resize-none"
      />
      <input
        type="text"
        placeholder="💡 실무 팁 (선택사항)"
        value={step.tip ?? ""}
        onChange={(e) => onChange(idx, "tip", e.target.value)}
        className="w-full bg-[#1C1F2E] text-[#7A9BFF]/70 text-xs px-3 py-2 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#4B6CF7] placeholder-[#3E4155]"
      />
    </div>
  );
}

function BulkCopyPanel({ currentWorkspaceId }: { currentWorkspaceId: string }) {
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from("workspaces")
      .select("id, name")
      .then(({ data }) => {
        if (data) setWorkspaces(data);
      });
  }, []);

  const handleCopy = async () => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setCopying(true);
    setResult(null);
    const { data: srcData } = await supabase
      .from("hub_guide_overrides")
      .select("*")
      .eq("workspace_id", sourceId);
    if (!srcData || srcData.length === 0) {
      setResult("복사할 데이터가 없습니다.");
      setCopying(false);
      return;
    }
    const toInsert = srcData.map(row => ({
      ...row,
      id: undefined,
      workspace_id: targetId,
      updated_at: new Date().toISOString(),
    }));
    const { error } = await supabase
      .from("hub_guide_overrides")
      .upsert(toInsert, { onConflict: "workspace_id,task_id" });
    setResult(error ? `오류: ${error.message}` : `${srcData.length}개 가이드가 복사되었습니다.`);
    setCopying(false);
  };

  const otherWorkspaces = workspaces.filter(w => w.id !== currentWorkspaceId);

  return (
    <div className="bg-[#1C1F2E] border border-[#2A2D3E] rounded-xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 flex items-center justify-center shrink-0">
          <i className="ri-file-copy-line text-[#E8375F] text-sm"></i>
        </div>
        <div>
          <h4 className="text-[#111111] text-sm font-semibold">워크스페이스 가이드 일괄 복사</h4>
          <p className="text-[#3E4155] text-[11px]">한 워크스페이스의 모든 가이드 수정 내용을 다른 워크스페이스에 그대로 적용합니다</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-[#5A5E72] text-[11px] mb-1.5">복사 원본 워크스페이스</label>
          <select
            value={sourceId}
            onChange={e => setSourceId(e.target.value)}
            className="w-full bg-[#252830] text-white text-xs px-3 py-2 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] cursor-pointer"
          >
            <option value="">선택하세요</option>
            {workspaces.map(w => (
              <option key={w.id} value={w.id}>{w.name}{w.id === currentWorkspaceId ? " (현재)" : ""}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[#5A5E72] text-[11px] mb-1.5">복사 대상 워크스페이스</label>
          <select
            value={targetId}
            onChange={e => setTargetId(e.target.value)}
            className="w-full bg-[#252830] text-white text-xs px-3 py-2 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] cursor-pointer"
          >
            <option value="">선택하세요</option>
            {otherWorkspaces.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
      </div>
      {sourceId && targetId && sourceId !== targetId && (
        <div className="flex items-center gap-2 mb-3 text-[11px] text-[#5A5E72] bg-[#252830] border border-[#2A2D3E] rounded-lg px-3 py-2">
          <i className="ri-information-line text-amber-400/60"></i>
          <span>
            <strong className="text-[#E8375F]">{workspaces.find(w => w.id === sourceId)?.name}</strong>의 가이드를{" "}
            <strong className="text-white">{workspaces.find(w => w.id === targetId)?.name}</strong>에 덮어씁니다. 기존 수정 내용은 덮어쓰여집니다.
          </span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleCopy}
          disabled={!sourceId || !targetId || sourceId === targetId || copying}
          className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg bg-[#E8375F] hover:bg-[#FF4B7A] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className={copying ? "ri-loader-4-line animate-spin" : "ri-file-copy-line"}></i>
          {copying ? "복사 중..." : "복사 실행"}
        </button>
        {result && (
          <span className={`text-[11px] ${result.startsWith("오류") ? "text-red-400/80" : "text-emerald-400/80"}`}>
            <i className={`${result.startsWith("오류") ? "ri-error-warning-line" : "ri-check-line"} mr-1`}></i>
            {result}
          </span>
        )}
      </div>
    </div>
  );
}

export default function HubGuideEditor({ workspaceId }: Props) {
  const [activeHub, setActiveHub] = useState<HubKey>("ar");
  const [selectedTaskId, setSelectedTaskId] = useState<string>(arTasks[0]?.id ?? "");
  const [overrides, setOverrides] = useState<Record<string, OverrideRecord>>({});
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showBulkCopy, setShowBulkCopy] = useState(false);

  const fetchOverrides = useCallback(async () => {
    if (!workspaceId) return;
    const { data } = await supabase
      .from("hub_guide_overrides")
      .select("*")
      .eq("workspace_id", workspaceId);
    if (data) {
      const map: Record<string, OverrideRecord> = {};
      data.forEach((row) => {
        map[row.task_id] = {
          task_id: row.task_id,
          hub: row.hub as HubKey,
          title: row.title ?? "",
          detail: row.detail ?? "",
          caution: row.caution ?? "",
          reference_text: row.reference_text ?? "",
          steps: (row.steps as TaskStep[]) ?? [],
        };
      });
      setOverrides(map);
    }
  }, [workspaceId]);

  useEffect(() => { fetchOverrides(); }, [fetchOverrides]);

  useEffect(() => {
    const allTasks = [...arTasks, ...concertTasks, ...foundationTasks, ...ipTasks, ...campaignTasks];
    const base = allTasks.find((t) => t.id === selectedTaskId);
    if (!base) return;
    const ov = overrides[selectedTaskId];
    setForm({
      task_id: selectedTaskId,
      hub: activeHub,
      title: ov ? ov.title : base.title,
      detail: ov ? ov.detail : base.detail,
      caution: ov ? ov.caution : base.caution ?? "",
      reference_text: ov ? ov.reference_text : base.reference ?? "",
      steps: ov ? ov.steps : (base.steps ?? []).map((s) => ({ ...s })),
    });
  }, [selectedTaskId, overrides, activeHub]);

  const isCustomized = (taskId: string) => !!overrides[taskId];

  const handleSave = async () => {
    if (!form || !workspaceId) return;
    setSaving(true);
    const { error } = await supabase.from("hub_guide_overrides").upsert(
      {
        workspace_id: workspaceId,
        task_id: form.task_id,
        hub: form.hub,
        hub_type: form.hub,
        title: form.title,
        detail: form.detail,
        caution: form.caution,
        reference_text: form.reference_text,
        steps: form.steps,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "workspace_id,task_id" }
    );
    if (!error) {
      setOverrides((prev) => ({ ...prev, [form.task_id]: { ...form } }));
      setSavedId(form.task_id);
      setTimeout(() => setSavedId(null), 2500);
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!workspaceId || !form) return;
    await supabase.from("hub_guide_overrides").delete()
      .eq("workspace_id", workspaceId).eq("task_id", selectedTaskId);
    const next = { ...overrides };
    delete next[selectedTaskId];
    setOverrides(next);
    setShowResetConfirm(false);
  };

  const updateStep = (idx: number, field: keyof TaskStep, val: string) => {
    if (!form) return;
    const steps = form.steps.map((s, i) =>
      i === idx ? { ...s, [field]: val } : s
    );
    setForm({ ...form, steps });
  };

  const addStep = () => {
    if (!form) return;
    const num = form.steps.length + 1;
    setForm({
      ...form,
      steps: [...form.steps, { num, title: "", detail: "", tip: "" }],
    });
  };

  const removeStep = (idx: number) => {
    if (!form) return;
    const steps = form.steps
      .filter((_, i) => i !== idx)
      .map((s, i) => ({ ...s, num: i + 1 }));
    setForm({ ...form, steps });
  };

  const currentTasks = hubConfig[activeHub].tasks;
  const customizedCount = Object.keys(overrides).length;
  const totalTasks = Object.values(hubConfig).reduce((s, h) => s + h.tasks.length, 0);

  return (
    <div className="flex flex-col h-full" style={{ minHeight: "calc(100vh - 220px)" }}>
      {/* Bulk Copy Toggle */}
      <div className="px-6 pt-4 pb-0">
        <button
          type="button"
          onClick={() => setShowBulkCopy(v => !v)}
          className="flex items-center gap-2 text-xs text-[#5A5E72] hover:text-white border border-[#2A2D3E] hover:border-white/20 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap mb-3"
        >
          <i className={`ri-file-copy-line ${showBulkCopy ? "text-[#E8375F]" : ""}`}></i>
          워크스페이스 가이드 일괄 복사
          <i className={`${showBulkCopy ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
        </button>
        {showBulkCopy && <BulkCopyPanel currentWorkspaceId={workspaceId} />}
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* ── Left: Hub + Task selector ── */}
        <div className="w-64 shrink-0 border-r border-[#252830] flex flex-col">
          <div className="p-3 border-b border-[#252830]">
            <div className="text-[#3E4155] text-[10px] uppercase tracking-widest mb-2 px-1">허브 선택</div>
            <div className="grid grid-cols-2 gap-1">
              {(Object.keys(hubConfig) as HubKey[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setActiveHub(key); setSelectedTaskId(hubConfig[key].tasks[0]?.id ?? ""); }}
                  className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs cursor-pointer whitespace-nowrap transition-all ${
                    activeHub === key
                      ? "bg-[#E8375F]/15 text-[#E8375F] font-semibold border border-[#E8375F]/20"
                      : "text-[#8B91A8] hover:text-white hover:bg-[#1C1F2E]"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${hubConfig[key].icon} text-sm`}></i>
                  </div>
                  {hubConfig[key].shortLabel}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            {currentTasks.map((task) => {
              const custom = isCustomized(task.id);
              const isSelected = selectedTaskId === task.id;
              return (
                <button
                  key={task.id}
                  type="button"
                  onClick={() => setSelectedTaskId(task.id)}
                  className={`w-full text-left px-4 py-3 transition-all cursor-pointer border-b border-white/[0.03] ${
                    isSelected
                      ? "bg-[#E8375F]/10 border-l-2 border-l-[#E8375F] pl-3.5"
                      : "hover:bg-[#1C1F2E] border-l-2 border-l-transparent"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={`text-xs leading-snug flex-1 ${
                      isSelected ? "text-[#F2F2F2]" : "text-[#AAAAAA]"
                    }`}
                  >
                    {task.title}
                  </span>
                  {custom && (
                    <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-[#E8375F] mt-1.5"></span>
                  )}
                </div>
                <div className="text-[10px] mt-1 flex items-center gap-1">
                  {custom ? (
                    <span className="text-[#E8375F]/70">
                      <i className="ri-edit-line mr-0.5"></i>수정됨
                    </span>
                  ) : (
                    <span className="text-[#3E4155]">기본 내용</span>
                  )}
                </div>
              </button>
              );
            })}
          </div>
          <div className="border-t border-[#252830] px-4 py-3">
            <div className="text-[#3E4155] text-[10px]">
              전체 {totalTasks}개 중 <span className="text-[#E8375F]">{customizedCount}개</span> 수정됨
            </div>
          </div>
        </div>

        {/* ── Right: Editor ── */}
        <div className="flex-1 overflow-y-auto">
          {form ? (
            <div className="p-6 max-w-2xl">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[#5A5E72] text-xs">
                      {hubConfig[activeHub].label}
                    </span>
                    {isCustomized(form.task_id) && (
                      <span className="text-[10px] text-[#E8375F] bg-[#E8375F]/10 border border-[#E8375F]/20 px-1.5 py-0.5 rounded">
                        수정됨
                      </span>
                    )}
                  </div>
                  <h3 className="text-[#111111] font-semibold text-base">
                    가이드 내용 편집
                  </h3>
                  <p className="text-[#3E4155] text-xs mt-1">
                    저장 시 이 워크스페이스에 참여한 학생들에게 즉시 적용됩니다
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isCustomized(form.task_id) && !showResetConfirm && (
                    <button
                      type="button"
                      onClick={() => setShowResetConfirm(true)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-white/[0.08] text-[#5A5E72] hover:text-[#8B91A8] hover:border-white/[0.15] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      기본값으로 초기화
                    </button>
                  )}
                  {showResetConfirm && (
                    <div className="flex items-center gap-2">
                      <span className="text-[#E8375F] text-xs">초기화할까요?</span>
                      <button
                        type="button"
                        onClick={handleReset}
                        className="text-xs px-2.5 py-1.5 rounded-lg bg-[#E8375F]/15 text-[#E8375F] border border-[#E8375F]/20 cursor-pointer whitespace-nowrap"
                      >
                        확인
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowResetConfirm(false)}
                        className="text-xs px-2.5 py-1.5 rounded-lg border border-white/[0.08] text-[#5A5E72] cursor-pointer whitespace-nowrap"
                      >
                        취소
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className={`flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap font-semibold disabled:opacity-50 ${
                      savedId === form.task_id
                        ? "bg-emerald-500/80 text-white"
                        : "bg-[#E8375F] hover:bg-[#FF4B7A] text-white"
                    }`}
                  >
                    <i
                      className={
                        savedId === form.task_id
                          ? "ri-check-line"
                          : "ri-save-line"
                      }
                    ></i>
                    {saving
                      ? "저장 중..."
                      : savedId === form.task_id
                      ? "저장됨"
                      : "저장하기"}
                  </button>
                </div>
              </div>

              {/* ── 기본 정보 ── */}
              <div className="space-y-4 mb-6">
                <div className="text-[#3E4155] text-[10px] uppercase tracking-widest font-medium flex items-center gap-2">
                  <i className="ri-information-line text-sm"></i>기본 정보
                </div>
                <div>
                  <label className="block text-[#8B91A8] text-[11px] font-medium mb-1.5">
                    할 일 제목
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full bg-[#1C1F2E] text-white text-sm px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder-[#3E4155]"
                  />
                </div>
                <div>
                  <label className="block text-[#8B91A8] text-[11px] font-medium mb-1.5">
                    업무 설명
                  </label>
                  <textarea
                    value={form.detail}
                    onChange={(e) => setForm({ ...form, detail: e.target.value })}
                    rows={3}
                    className="w-full bg-[#1C1F2E] text-white text-sm px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder-[#3E4155] resize-none"
                  />
                </div>
              </div>

              {/* ── 단계별 가이드 ── */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[#3E4155] text-[10px] uppercase tracking-widest font-medium flex items-center gap-2">
                    <i className="ri-route-line text-sm"></i>단계별 업무 가이드
                    <span className="text-[#E8375F] text-[10px] font-normal normal-case">
                      ({form.steps.length}단계)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={addStep}
                    className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg bg-[#E8375F]/10 text-[#E8375F] border border-[#E8375F]/20 hover:bg-[#E8375F]/20 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>단계 추가
                  </button>
                </div>

                <div className="space-y-3">
                  {form.steps.map((step, idx) => (
                    <StepEditor
                      key={idx}
                      step={step}
                      idx={idx}
                      onChange={updateStep}
                      onRemove={removeStep}
                    />
                  ))}
                  {form.steps.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-8 bg-[#1C1F2E] border border-dashed border-[#2A2D3E] rounded-xl">
                      <i className="ri-list-check text-2xl text-[#2A2D3E] mb-2"></i>
                      <p className="text-[#3E4155] text-xs">
                        단계가 없습니다. '단계 추가' 버튼으로 업무 가이드를 작성하세요.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* ── 주의사항 ── */}
              <div className="mb-4">
                <label className="block text-[#8B91A8] text-[11px] font-medium mb-1.5 flex items-center gap-1.5">
                  <i className="ri-error-warning-line text-amber-400/70"></i>
                  주의사항 (선택)
                </label>
                <textarea
                  value={form.caution}
                  onChange={(e) => setForm({ ...form, caution: e.target.value })}
                  placeholder="학생들이 특히 주의해야 할 점을 입력하세요"
                  rows={2}
                  className="w-full bg-[#1C1F2E] text-white text-sm px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder-[#3E4155] resize-none"
                />
              </div>

              {/* ── 참고자료 ── */}
              <div className="mb-6">
                <label className="block text-[#8B91A8] text-[11px] font-medium mb-1.5 flex items-center gap-1.5">
                  <i className="ri-book-open-line text-[#555555]"></i>
                  참고자료 (선택)
                </label>
                <textarea
                  value={form.reference_text}
                  onChange={(e) =>
                    setForm({ ...form, reference_text: e.target.value })
                  }
                  placeholder="참고할 법령, 가이드, 링크 등을 입력하세요"
                  rows={2}
                  className="w-full bg-[#1C1F2E] text-white text-sm px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder-[#3E4155] resize-none"
                />
              </div>

              {/* Bottom Save */}
              <div className="flex justify-end pt-4 border-t border-[#252830]">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap font-semibold text-sm disabled:opacity-50 ${
                    savedId === form.task_id
                      ? "bg-emerald-500/80 text-white"
                      : "bg-[#E8375F] hover:bg-[#FF4B7A] text-white"
                  }`}
                >
                  <i
                    className={
                      savedId === form.task_id
                        ? "ri-check-double-line"
                        : "ri-save-line"
                    }
                  ></i>
                  {saving
                    ? "저장 중..."
                    : savedId === form.task_id
                    ? "저장 완료!"
                    : "변경사항 저장"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-24">
              <i className="ri-edit-box-line text-3xl text-[#2A2A2A] mb-3"></i>
              <p className="text-[#5A5E72] text-sm">
                왼쪽에서 태스크를 선택하세요
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
